import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon, XIcon } from '@persona-id/design-tokens/icons';
import { useCallback, useEffect, useState, VFC } from 'react';
import styled from 'styled-components';

import HandlebarFormControl from 'dashboard/components/HandlebarFormControl';
import ReactSelect from 'dashboard/components/ReactSelect';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { JsonSchemaCustomTypes } from 'lib/fields';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';

export type KeyOptionType = {
  label: string;
  value: string;
  type: JsonSchemaTypes | JsonSchemaCustomTypes;
};

interface EditKeyValuePairsPanelProps {
  editable: boolean;
  jsonSchema: JsonSchema;
  keyOptions?: KeyOptionType[];
  keyValueObject: Record<string, string>;
  tokensToDisplayValue: Record<string, string>;
  updateObject: (keyValueObject: Record<string, string>) => void;
}

const KeyValueRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  &:not(:last-child) {
    margin-bottom: ${Spacing[2]};
  }
`;

const HandlebarFormControlContainer = styled.div`
  min-width: 50%;
`;

const KeyInput = styled(Input)`
  min-height: 100%;
  margin-right: ${Spacing[2]};
`;

const ReactSelectKeyInput = styled(ReactSelect<string>)`
  min-height: 100%;
  margin-right: ${Spacing[2]};
`;

const CenteredIcon = styled.div`
  cursor: pointer;
  margin-left: ${Spacing[2]};
`;

const EditKeyValuePairsPanel: VFC<EditKeyValuePairsPanelProps> = ({
  editable,
  jsonSchema,
  keyOptions = [],
  keyValueObject,
  tokensToDisplayValue,
  updateObject,
}) => {
  const [keyValueObjectArray, setKeyValueArray] = useState(Object.entries(keyValueObject));

  useEffect(() => {
    if (keyValueObjectArray.length === 0) {
      addNewKeyValue();
    }
  }, []);

  const getAllowedTypesForKey = useCallback(
    (currentKey: string) => {
      const currentOption = keyOptions.find((option) => option.value === currentKey);
      return currentOption ? [currentOption.type] : [];
    },
    [keyOptions],
  );

  const getAvailableKeyOptions = useCallback(
    (currentKey: string) => {
      return keyOptions.filter((option) => {
        return !Object.keys(keyValueObject).includes(option.value) || option.value === currentKey;
      });
    },
    [keyOptions, keyValueObject],
  );

  const updateKey = useCallback(
    (idx: number, value: string) => {
      const newKV = [...keyValueObjectArray];
      newKV[idx][0] = value;
      setKeyValueArray(newKV);

      // to prevent key overide in redux for the object
      if (!keyValueObject[value]) {
        updateObject(Object.fromEntries(keyValueObjectArray));
      }
    },
    [keyValueObject, keyValueObjectArray, updateObject],
  );

  const selectKey = useCallback(
    (idx: number, value: string) => {
      updateKey(idx, value);
    },
    [updateKey],
  );

  const updateValue = useCallback(
    (idx: number, value: string) => {
      const newKV = [...keyValueObjectArray];
      newKV[idx][1] = value;
      updateObject(Object.fromEntries(keyValueObjectArray));
    },
    [keyValueObjectArray, updateObject],
  );

  const addNewKeyValue = useCallback(() => {
    setKeyValueArray([...keyValueObjectArray, ['', '']]);
    updateObject(Object.fromEntries(keyValueObjectArray));
  }, [keyValueObjectArray, updateObject]);

  const removeKeyValue = useCallback(
    (idx: number) => {
      const newKV = [...keyValueObjectArray];
      newKV.splice(idx, 1);
      setKeyValueArray(newKV);
      updateObject(Object.fromEntries(newKV));
    },
    [keyValueObjectArray, updateObject],
  );

  return (
    <>
      {keyValueObjectArray.map((record, idx) => (
        <KeyValueRow key={idx}>
          {!!keyOptions.length && keyOptions.length !== Object.keys(keyValueObject).length ? (
            <ReactSelectKeyInput
              id={`edit-key-value-panel__key-input-${idx}`}
              aria-label={`property name ${idx}`}
              value={record[0]}
              options={getAvailableKeyOptions(record[0])}
              menuPlacement='bottom'
              onChange={(val: string) => selectKey(idx, val)}
              disabled={!editable}
              placeholder='Property Name'
            />
          ) : (
            <KeyInput
              size='md'
              type='string'
              key={`header-key-${idx}`}
              value={record[0]}
              onChange={(e) => updateKey(idx, e.target.value)}
              disabled={!editable}
              placeholder='Property Name'
              id={`edit-key-value-panel__key-input-${idx}`}
            />
          )}

          <HandlebarFormControlContainer>
            <HandlebarFormControl
              size='md'
              type='input'
              key={`header-value-${idx}`}
              value={record[1]}
              updateValue={(value: string) => {
                updateValue(idx, value);
              }}
              editable={editable}
              allowedTypes={getAllowedTypesForKey(record[0])}
              jsonSchema={jsonSchema}
              tokensToDisplayValue={tokensToDisplayValue}
              placeholder='Property Value'
              id={`edit-key-value-panel__value-input-${idx}`}
              style={{ maxHeight: '100px', overflow: 'hidden' }}
            />
          </HandlebarFormControlContainer>
          {editable && (
            <CenteredIcon
              aria-label='Delete this Field'
              id={`edit-key-value-panel__delete-${idx}`}
              role='button'
              tabIndex={0}
              onClick={() => removeKeyValue(idx)}
            >
              <XIcon />
            </CenteredIcon>
          )}
        </KeyValueRow>
      ))}
      <div>
        <Spacer height={2} />
        <Button
          variant='basic'
          width='auto'
          disabled={!editable}
          onClick={addNewKeyValue}
          id='edit-key-value-panel__add-button'
          prefixIcon={<PlusIcon />}
        >
          Add Property
        </Button>
      </div>
    </>
  );
};

export default EditKeyValuePairsPanel;
